import React, { useState, useEffect } from 'react';
import {
  Card,
  Row,
  Col,
  DatePicker,
  Select,
  Button,
  Table,
  Statistic,
  Tag,
  Space,
  message,
  Empty,
  Tooltip,
  Spin,
} from 'antd';
import dayjs from 'dayjs';
import 'dayjs/locale/zh-cn';
import {
  SearchOutlined,
  ReloadOutlined,
  BarChartOutlined,
  ClockCircleOutlined,
  UserOutlined,
  AppstoreOutlined,
  EnvironmentOutlined,
} from '@ant-design/icons';
import { userStatsAPI } from '../../services/api';
import './UserStats.css';

const { RangePicker } = DatePicker;
const { Option } = Select;

// 设置 dayjs 为中文
dayjs.locale('zh-cn');

const UserStats = () => {
  const [loading, setLoading] = useState(false);
  const [statsData, setStatsData] = useState([]);
  const [queryParams, setQueryParams] = useState({
    start_date: '',
    end_date: '',
    category: 0,
  });
  const [dateRange, setDateRange] = useState([null, null]);
  const [summaryData, setSummaryData] = useState({
    totalCount: 0,
    totalTime: 0,
    ipCount: 0,
    moduleCount: 0,
  });

  // 类别配置
  const categoryOptions = [
    { value: 0, label: '全部类别', color: 'default' },
    { value: 1, label: '使用模块', color: 'blue' },
    { value: 2, label: '观看视频', color: 'green' },
    { value: 3, label: '下载资源', color: 'orange' },
    { value: 4, label: '语音评测', color: 'purple' },
  ];

  // 获取类别标签
  const getCategoryTag = (category) => {
    const config = categoryOptions.find(item => item.value === category);
    return config ? (
      <Tag color={config.color}>{config.label}</Tag>
    ) : (
      <Tag>{category}</Tag>
    );
  };

  // 格式化时间（秒转小时分钟）
  const formatTime = (seconds) => {
    if (!seconds) return '0秒';
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    
    if (hours > 0) {
      return `${hours}小时${minutes}分${secs}秒`;
    } else if (minutes > 0) {
      return `${minutes}分${secs}秒`;
    } else {
      return `${secs}秒`;
    }
  };

  // 计算汇总数据
  const calculateSummary = (data) => {
    if (!data || data.length === 0) {
      setSummaryData({
        totalCount: 0,
        totalTime: 0,
        ipCount: 0,
        moduleCount: 0,
      });
      return;
    }

    const totalCount = data.reduce((sum, item) => sum + item.count, 0);
    const totalTime = data.reduce((sum, item) => sum + item.total_use_time, 0);
    const uniqueIps = new Set(data.map(item => item.ip_id)).size;
    const uniqueModules = new Set(data.map(item => item.use_name)).size;

    setSummaryData({
      totalCount,
      totalTime,
      ipCount: uniqueIps,
      moduleCount: uniqueModules,
    });
  };

  // 查询统计数据
  const fetchStats = async (params = queryParams) => {
    setLoading(true);
    try {
      const response = await userStatsAPI.getUserStats(params);
      
      if (response.success) {
        setStatsData(response.data.stats || []);
        calculateSummary(response.data.stats || []);
        message.success('数据加载成功');
      } else {
        message.error(response.message || '数据加载失败');
      }
    } catch (error) {
      console.error('查询统计数据失败:', error);
      message.error('查询失败，请稍后重试');
      setStatsData([]);
      calculateSummary([]);
    } finally {
      setLoading(false);
    }
  };

  // 格式化日期为 YYYY-MM-DD
  const formatDate = (date) => {
    if (!date) return '';
    // 处理Date对象或dayjs对象
    if (date.format) {
      return date.format('YYYY-MM-DD');
    }
    // 处理原生Date对象
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // 处理查询
  const handleSearch = () => {
    const params = { ...queryParams };
    
    // 转换日期格式
    if (dateRange && dateRange[0] && dateRange[1]) {
      params.start_date = formatDate(dateRange[0]);
      params.end_date = formatDate(dateRange[1]);
    } else {
      params.start_date = '';
      params.end_date = '';
    }

    setQueryParams(params);
    fetchStats(params);
  };

  // 重置查询
  const handleReset = () => {
    setDateRange([null, null]);
    setQueryParams({
      start_date: '',
      end_date: '',
      category: 0,
    });
    setStatsData([]);
    setSummaryData({
      totalCount: 0,
      totalTime: 0,
      ipCount: 0,
      moduleCount: 0,
    });
  };

  // 快捷日期选择
  const handleQuickDate = (days) => {
    const end = dayjs();
    const start = dayjs().subtract(days - 1, 'day');
    setDateRange([start, end]);
    
    // 自动触发查询
    const params = {
      ...queryParams,
      start_date: start.format('YYYY-MM-DD'),
      end_date: end.format('YYYY-MM-DD'),
    };
    setQueryParams(params);
    fetchStats(params);
  };

  // 表格列配置
  const columns = [
    {
      title: '用户',
      dataIndex: 'username',
      key: 'username',
      width: 120,
      fixed: 'left',
      render: (text, record) => (
        <Space>
          <UserOutlined style={{ color: record.user_id > 0 ? '#52c41a' : '#999' }} />
          {record.user_id > 0 ? (text || `用户${record.user_id}`) : '未登录'}
        </Space>
      ),
      filters: [
        { text: '已登录', value: 'logged' },
        { text: '未登录', value: 'anonymous' },
      ],
      onFilter: (value, record) => value === 'logged' ? record.user_id > 0 : record.user_id === 0,
    },
    {
      title: 'IP地址',
      dataIndex: 'ip',
      key: 'ip',
      width: 150,
      render: (text, record) => (
        <Tooltip title={record.ip_detail}>
          <Space>
            <EnvironmentOutlined style={{ color: '#1890ff' }} />
            {text}
          </Space>
        </Tooltip>
      ),
    },
    {
      title: '地区',
      dataIndex: 'ip_detail',
      key: 'ip_detail',
      width: 150,
      ellipsis: true,
    },
    {
      title: '类别',
      dataIndex: 'category',
      key: 'category',
      width: 120,
      align: 'center',
      render: (category) => getCategoryTag(category),
      filters: categoryOptions.slice(1).map(item => ({
        text: item.label,
        value: item.value,
      })),
      onFilter: (value, record) => record.category === value,
    },
    {
      title: '模块名称',
      dataIndex: 'use_name',
      key: 'use_name',
      width: 200,
      ellipsis: true,
    },
    {
      title: '使用次数',
      dataIndex: 'count',
      key: 'count',
      width: 120,
      align: 'center',
      sorter: (a, b) => a.count - b.count,
      render: (count) => (
        <Tag color="cyan" style={{ fontSize: '14px', padding: '4px 10px' }}>
          {count} 次
        </Tag>
      ),
    },
    {
      title: '总使用时长',
      dataIndex: 'total_use_time',
      key: 'total_use_time',
      width: 180,
      align: 'center',
      sorter: (a, b) => a.total_use_time - b.total_use_time,
      render: (time) => (
        <Tooltip title={`${time}秒`}>
          <Space>
            <ClockCircleOutlined style={{ color: '#52c41a' }} />
            {formatTime(time)}
          </Space>
        </Tooltip>
      ),
    },
  ];

  return (
    <div className="user-stats-container">
      <div className="stats-header">
        <h2>
          <BarChartOutlined style={{ marginRight: 8 }} />
          用户使用统计查询
        </h2>
        <p className="stats-description">
          查询和分析用户使用记录，支持按时间范围和类别筛选
        </p>
      </div>

      {/* 查询条件区域 */}
      <Card className="search-card" bordered={false}>
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          <Row gutter={[16, 16]} align="middle">
            <Col xs={24} sm={24} md={10} lg={8}>
              <div className="form-item">
                <label>时间范围：</label>
                <RangePicker
                  value={dateRange}
                  onChange={setDateRange}
                  format="YYYY-MM-DD"
                  placeholder={['开始日期', '结束日期']}
                  size="large"
                  style={{ width: '100%' }}
                />
              </div>
            </Col>
            <Col xs={24} sm={12} md={6} lg={4}>
              <div className="form-item">
                <label>类别：</label>
                <Select
                  value={queryParams.category}
                  onChange={(value) => setQueryParams({ ...queryParams, category: value })}
                  size="large"
                  style={{ width: '100%' }}
                >
                  {categoryOptions.map(option => (
                    <Option key={option.value} value={option.value}>
                      {option.label}
                    </Option>
                  ))}
                </Select>
              </div>
            </Col>
            <Col xs={24} sm={12} md={8} lg={6}>
              <div className="form-item">
                <label style={{ opacity: 0 }}>占位</label>
                <Space>
                  <Button
                    type="primary"
                    icon={<SearchOutlined />}
                    onClick={handleSearch}
                    loading={loading}
                    size="large"
                  >
                    查询
                  </Button>
                  <Button 
                    icon={<ReloadOutlined />} 
                    onClick={handleReset}
                    size="large"
                  >
                    重置
                  </Button>
                </Space>
              </div>
            </Col>
          </Row>

          {/* 快捷日期选择 */}
          <Row gutter={[8, 8]}>
            <Col>
              <span style={{ marginRight: 8, color: '#666' }}>快捷选择：</span>
            </Col>
            <Col>
              <Space wrap>
                <Button size="small" onClick={() => handleQuickDate(1)}>
                  今天
                </Button>
                <Button size="small" onClick={() => handleQuickDate(7)}>
                  最近7天
                </Button>
                <Button size="small" onClick={() => handleQuickDate(30)}>
                  最近30天
                </Button>
                <Button size="small" onClick={() => handleQuickDate(90)}>
                  最近90天
                </Button>
              </Space>
            </Col>
          </Row>
        </Space>
      </Card>

      {/* 统计卡片区域 */}
      {statsData.length > 0 && (
        <Row gutter={[16, 16]} style={{ marginTop: 24 }}>
          <Col xs={24} sm={12} lg={6}>
            <Card className="stat-card" bordered={false}>
              <Statistic
                title="总使用次数"
                value={summaryData.totalCount}
                prefix={<BarChartOutlined />}
                valueStyle={{ color: '#3f8600' }}
                suffix="次"
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <Card className="stat-card" bordered={false}>
              <Statistic
                title="总使用时长"
                value={summaryData.totalTime}
                prefix={<ClockCircleOutlined />}
                valueStyle={{ color: '#1890ff' }}
                suffix="秒"
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <Card className="stat-card" bordered={false}>
              <Statistic
                title="IP数量"
                value={summaryData.ipCount}
                prefix={<UserOutlined />}
                valueStyle={{ color: '#722ed1' }}
                suffix="个"
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <Card className="stat-card" bordered={false}>
              <Statistic
                title="模块数量"
                value={summaryData.moduleCount}
                prefix={<AppstoreOutlined />}
                valueStyle={{ color: '#eb2f96' }}
                suffix="个"
              />
            </Card>
          </Col>
        </Row>
      )}

      {/* 数据表格区域 */}
      <Card
        className="data-table-card"
        title={
          <span>
            <AppstoreOutlined style={{ marginRight: 8 }} />
            统计详情
            {statsData.length > 0 && (
              <Tag color="blue" style={{ marginLeft: 8 }}>
                共 {statsData.length} 条记录
              </Tag>
            )}
          </span>
        }
        bordered={false}
        style={{ marginTop: 24 }}
      >
        <Spin spinning={loading}>
          {statsData.length > 0 ? (
            <Table
              columns={columns}
              dataSource={statsData.map((item, index) => ({
                ...item,
                key: `${item.ip_id}-${item.category}-${item.use_name}-${index}`,
              }))}
              pagination={{
                showSizeChanger: true,
                showQuickJumper: true,
                showTotal: (total) => `共 ${total} 条`,
                pageSizeOptions: ['10', '20', '50', '100'],
                defaultPageSize: 20,
              }}
              scroll={{ x: 1000 }}
              size="middle"
            />
          ) : (
            <Empty
              description={
                <span>
                  {loading ? '加载中...' : '暂无数据，请选择查询条件后点击"查询"按钮'}
                </span>
              }
              style={{ padding: '60px 0' }}
            />
          )}
        </Spin>
      </Card>
    </div>
  );
};

export default UserStats;
